import React, {useCallback, useMemo, useState} from 'react';

import {
  Props as SelectBoxProps,
  StartingPoint,
} from '#/form/selectable-input/SelectBox';
import {memo} from '_/utilities/memo';
import {fixItemTextMargins} from '_/utilities/sharedFilterItemHelpers';
import {Positioning} from '_/utilities/types';

import FilterItem, {Item} from './Item';
import {StyledFilter, StyledSelectBox} from './styles';

/** @deprecated Use EFilter instead. */
interface Props {
  items: Item[];
  activeItems: Item[];
  onActiveItemChange: (selection: Item[]) => void;
  className?: string;
}

/** @deprecated Use EFilter instead. */
const FilterSelectBox = memo(
  ({
    item,
    onActiveItemChange,
    ...props
  }: SelectBoxProps<Item> & {
    active: boolean;
    item: Item;
    onActiveItemChange: (selection: Item[]) => void;
  }): React.ReactElement => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const handleChange = useCallback(
      (selectedItem: Item) => {
        onActiveItemChange([item, selectedItem]);
      },
      [onActiveItemChange, item]
    );

    const handleExpandChange = useCallback(
      (expanded: boolean) => {
        setIsExpanded(expanded);
      },
      [setIsExpanded]
    );

    return (
      <StyledSelectBox
        {...props}
        expanded={isExpanded}
        onExpandedChange={handleExpandChange}
        onItemChange={handleChange}
      />
    );
  }
);

/**
 * @deprecated Use EFilter instead.
 *
 * Renders items in a horizontal bar, if an item has sub items provided in its "subItems" attribute, it will be rendered
 * as a selectbox, and its sub items will be rendered as the items of the select box.
 * Let's assume the provided "items" is {"id": "a", "subItems": [{id: "a1"}], {"id": "b"}}
 * An array like ["a", "a1"] is a an item path indicating "a1" from "a" is selected, another item path would be ["b"]
 * which simply means "b" is selected.
 * The whole filter component and its children items/select boxes are composing a whole and at any time, only one item-
 * path can be selected, so if a sub item of a select box is selected and the user activates a sub-item of another
 * select box in this filter bar, the first select box will reset to its placeholder.
 */
export function FilterComponent({
  items,
  /**
   * Current active item path.
   */
  activeItems,
  /**
   * Will be called when user activates another item path.
   */
  onActiveItemChange,
  className,
}: Props): React.ReactElement {
  const handleItemChange = useCallback(
    (selection: Item) => {
      onActiveItemChange([selection]);
    },
    [onActiveItemChange]
  );

  const editedItems = useMemo(
    () =>
      items.map((item) => {
        const editedItem = {...item};
        if (item.subItems) {
          editedItem.subItems = fixItemTextMargins(item.subItems);
        }
        return editedItem;
      }),
    [items]
  );

  return (
    <div className={className}>
      {editedItems.map((item) => {
        if (item.subItems || item.source) {
          return (
            <FilterSelectBox
              key={item.id}
              active={activeItems.length > 0 && activeItems[0] === item}
              item={item}
              items={item.subItems}
              placeholder={item.text}
              placement={Positioning.BottomLeftAlignDown}
              selectedItem={activeItems.length > 1 ? activeItems[1] : undefined}
              source={item.source}
              startingPoint={StartingPoint.Dropdown}
              onActiveItemChange={onActiveItemChange}
            />
          );
        } else {
          return (
            <FilterItem
              key={item.id}
              active={item.id === activeItems[0]?.id}
              item={item}
              onActivate={handleItemChange}
            />
          );
        }
      })}
    </div>
  );
}

/** @deprecated Use EFilter instead. */
export default memo(FilterComponent, StyledFilter);
